import { Component, Signal, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { UiLibraryModule } from "../../../../template-ui.module";
import { ButtonConfig } from "../../../../types";
import { DocMeta } from "../../../../decorators";
import { MessageService } from 'primeng/api';
import { ToastModule } from "primeng/toast";
import { ToggleSwitchModule } from "primeng/toggleswitch";
import { FormsModule } from "@angular/forms";
import { toObservable, toSignal } from "@angular/core/rxjs-interop";
import { of } from "rxjs";

@DocMeta({
  hide: true,
  showcaseTitle: "Configuration Options",
  showcaseDesc:`
  This sample demonstrates using the config object for the button instead of putting everything in the HTML.
  `
})
@Component({
    selector: 'ui-buttons-with-config, ez-buttons-with-config',
    templateUrl: './buttons-with-config.component.html',
    styleUrl: './buttons-with-config.component.scss',
    imports: [
        // Angular
        CommonModule,
        FormsModule, // <-- Needed for toggle switch
        // Prime
        ToastModule, ToggleSwitchModule,
        // EZ
        UiLibraryModule
    ],
    providers: [MessageService]
})
export class ButtonsWithConfigComponent {

  // Use of a signal for the disabled property allows it to be reactive
  disabled = signal(false);

  disabled$ = toObservable(this.disabled);

  buttons: ButtonConfig[] = [
    {
      id: 'primary',
      buttonText: "I am a button configured with a config object",
      disabled: this.disabled,  // <-- passing a signal directly to a Reactive Input, this is the way.
      onClick: (but, event) => this.handleClick(but)
    },

    {
      id: 'route',
      buttonText: "I am an anchor tag. See the target <b>route</b> in the status bar.",
      disabled: () => this.disabled(),  // A Reactive Input can be bound to a function, although you might see a short delay, this is NOT the way
      routerLink: '/',
      onClick: (but, event) => this.handleClick(but)
    },

    {
      id: 'href',
      buttonText: "I am an anchor tag. See the target <b>href</b> in the status bar.",
      disabled: toSignal(this.disabled$) as Signal<boolean>,  // <-- a ReactiveInput can be bound to an observable using toSignal(), only if you must.
      href: 'http://google.com',
      target: "_blank",
      shiftUrl: 'http://microsoft.com',  // <-- Hold the shift key while clicking to go to a different url but you won't see the alternate URL in the status bar :(
      onClick: (but, event) => this.handleClick(but)
    },

    {
      id: 'forceButton',
      buttonText: "I am a link forced to be a button, you can't see the target in the status bar, where will I take you?",
      disabled: false,   // <-- can also just pass a constant, but don't expect it to change
      href: 'https://starwars.fandom.com/wiki/May_the_Force_be_with_you',
      target: "_blank",
      forceButton: true  // <-- forces <button> instead of <a> so you won't see the target in the status bar
    },
  ];

  constructor(private messageService: MessageService) {}

  handleClick(but?: ButtonConfig): void {
    this.messageService.add({
      key: 'button-toast',
      severity: 'info',
      summary: 'Button Clicked',
      detail: `${but?.buttonText || but?.id} button clicked!`,
      life: 1000
    });
  }
}
